import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Minus, Plus, Loader2 } from "lucide-react";
import { useCart } from "../context/CartContext";

const CartDrawer = () => {
    const { items, open, setOpen, updateQty, removeItem, subtotal, count } =
        useCart();
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const handleCheckout = () => {
        if (items.length === 0) return;
        setLoading(true);
        setOpen(false);
        navigate("/checkout");
        setLoading(false);
    };

    return (
        <>
            {open && (
                <div
                    data-testid="cart-backdrop"
                    className="fixed inset-0 bg-[#0A2342]/40 backdrop-blur-sm z-50"
                    onClick={() => setOpen(false)}
                />
            )}
            <aside
                data-testid="cart-drawer"
                className={`fixed top-0 right-0 h-full w-full sm:w-[420px] bg-[#FBF9F6] z-50 shadow-2xl transform transition-transform duration-500 ease-out ${
                    open ? "translate-x-0" : "translate-x-full"
                }`}
            >
                <div className="flex items-center justify-between px-6 py-5 border-b border-[#E5E0D8]">
                    <h3 className="font-serif-display text-2xl text-[#0A2342]">
                        Your Cart{" "}
                        <span className="text-sm text-[#5C6A79] font-sans align-middle">
                            ({count})
                        </span>
                    </h3>
                    <button
                        data-testid="cart-close-btn"
                        onClick={() => setOpen(false)}
                        className="p-2 text-[#0A2342] hover:text-[#9E5A35]"
                        aria-label="Close cart"
                    >
                        <X size={22} strokeWidth={1.5} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-6 h-[calc(100%-220px)]">
                    {items.length === 0 ? (
                        <div
                            data-testid="cart-empty-state"
                            className="text-center py-16"
                        >
                            <div className="font-serif-display text-2xl text-[#0A2342] mb-2">
                                Your cart is empty
                            </div>
                            <p className="text-sm text-[#5C6A79]">
                                Add a fob to begin.
                            </p>
                        </div>
                    ) : (
                        <ul className="space-y-6">
                            {items.map(({ product, quantity }) => (
                                <li
                                    key={product.id}
                                    data-testid={`cart-item-${product.id}`}
                                    className="flex gap-4"
                                >
                                    <div className="w-20 h-24 bg-white border border-[#E5E0D8] flex items-center justify-center overflow-hidden">
                                        <img
                                            src={product.image_url}
                                            alt={product.name}
                                            className="w-full h-full object-contain"
                                        />
                                    </div>
                                    <div className="flex-1">
                                        <div className="font-serif-display text-lg text-[#0A2342] leading-tight">
                                            {product.name}
                                        </div>
                                        <div className="text-sm text-[#5C6A79] mt-1">
                                            ${product.price.toFixed(2)}
                                        </div>
                                        <div className="mt-3 flex items-center gap-3">
                                            <div className="inline-flex items-center border border-[#E5E0D8] rounded-sm">
                                                <button
                                                    data-testid={`cart-qty-decrease-${product.id}`}
                                                    onClick={() =>
                                                        updateQty(
                                                            product.id,
                                                            quantity - 1,
                                                        )
                                                    }
                                                    className="p-1.5 text-[#0A2342]"
                                                    aria-label="Decrease"
                                                >
                                                    <Minus size={14} />
                                                </button>
                                                <span
                                                    data-testid={`cart-qty-${product.id}`}
                                                    className="px-3 text-sm"
                                                >
                                                    {quantity}
                                                </span>
                                                <button
                                                    data-testid={`cart-qty-increase-${product.id}`}
                                                    onClick={() =>
                                                        updateQty(
                                                            product.id,
                                                            quantity + 1,
                                                        )
                                                    }
                                                    className="p-1.5 text-[#0A2342]"
                                                    aria-label="Increase"
                                                >
                                                    <Plus size={14} />
                                                </button>
                                            </div>
                                            <button
                                                data-testid={`cart-remove-${product.id}`}
                                                onClick={() =>
                                                    removeItem(product.id)
                                                }
                                                className="text-xs text-[#5C6A79] hover:text-[#9E5A35] uppercase tracking-[0.15em]"
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="absolute bottom-0 left-0 right-0 border-t border-[#E5E0D8] bg-[#FBF9F6] px-6 py-5">
                    <div className="flex items-center justify-between mb-4">
                        <span className="text-xs tracking-[0.22em] uppercase text-[#5C6A79]">
                            Subtotal
                        </span>
                        <span
                            data-testid="cart-subtotal"
                            className="font-serif-display text-2xl text-[#0A2342]"
                        >
                            ${subtotal.toFixed(2)}
                        </span>
                    </div>
                    <button
                        data-testid="cart-checkout-btn"
                        onClick={handleCheckout}
                        disabled={items.length === 0 || loading}
                        className="btn-primary w-full justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        {loading ? (
                            <>
                                <Loader2 className="animate-spin" size={16} />
                                Redirecting…
                            </>
                        ) : (
                            "Checkout"
                        )}
                    </button>
                    <p className="text-[11px] text-[#5C6A79] text-center mt-3">
                        Secure checkout via Stripe
                    </p>
                </div>
            </aside>
        </>
    );
};

export default CartDrawer;
